import type { AxiosError } from 'axios';

import { DEFAULT_UPGRADE_PLAN_SLUG } from '../../constants/subscriptionCheckout';
import { useAuthStore } from '../../store/useAuthStore';
import { shouldSkipCustomerBillingFlow } from '../../utils/customerBillingProfile';
import { getPaymentBillingStatusCache } from '../../utils/paymentBillingCache';
import { getRenewalPlanSlug, mapSnapshotToBillingContext } from '../../utils/customerBillingAccess';
import { getCustomerSubscriptionCache } from '../../utils/customerSubscription';
import { buildAssinarPath, isBillingEnforcementEnabled } from '../../utils/planCheckout';
import { isSubscriptionPaymentGateWhitelisted } from '../../utils/resolvePostAuthPath';

interface ApiErrorBody {
  code?: string;
  message?: string;
}

const BILLING_REDIRECT_CODES = new Set(['SUBSCRIPTION_PAYMENT_REQUIRED', 'TRIAL_EXPIRED']);

export const shouldRedirectForBillingError = (error: AxiosError): boolean => {
  if (!isBillingEnforcementEnabled()) return false;
  if (error.response?.status !== 403) return false;
  const code = (error.response.data as ApiErrorBody | undefined)?.code;
  return typeof code === 'string' && BILLING_REDIRECT_CODES.has(code);
};

export const resolveBillingRedirectPath = (): string => {
  const subscriptionSnapshot = getCustomerSubscriptionCache();
  const billingContext = mapSnapshotToBillingContext(subscriptionSnapshot);
  const paymentBilling = getPaymentBillingStatusCache();

  const slug = getRenewalPlanSlug(billingContext, paymentBilling) ?? DEFAULT_UPGRADE_PLAN_SLUG;

  return buildAssinarPath(slug, 'subscribe');
};

export const maybeRedirectForBillingError = (error: AxiosError): boolean => {
  if (!shouldRedirectForBillingError(error)) return false;

  const user = useAuthStore.getState().user;
  if (shouldSkipCustomerBillingFlow(user)) return false;

  const pathname = window.location.pathname;
  if (pathname.startsWith('/consultant')) return false;
  if (isSubscriptionPaymentGateWhitelisted(pathname) || pathname === '/assinar') {
    return false;
  }

  const target = resolveBillingRedirectPath();
  window.location.assign(target);
  return true;
};
